<template>
  <div :class="['audio-text-sync', { 'dark-theme': isDark }]">
    <!-- 文字同步显示区 -->
    <div v-if="showText" class="text-sync-container" ref="textContainer">
      <div
        v-for="(item, index) in textSegments"
        :key="index"
        :class="['text-segment', {
          'active': isSegmentActive(item),
          'passed': isSegmentPassed(item),
          'robot': item.role === 1,
          'customer': item.role !== 1
        }]"
        @click="seekToSegment(item)"
      >
        <div class="segment-header">
          <span class="segment-role">
            {{ item.role === 1 ? '🤖 机器人' : '👤 客户' }}
          </span>
          <span class="segment-time">{{ formatTime(item.startTime) }}</span>
        </div>
        <div class="segment-content">
          <span
            v-for="(char, charIndex) in item.text.split('')"
            :key="charIndex"
            :class="['char', {
              'char-highlight': isCharHighlighted(item, charIndex)
            }]"
          >
            {{ char }}
          </span>
        </div>
      </div>
    </div>

    <!-- 当前播放位置指示 -->
    <div v-if="showText && currentSegment" class="current-position">
      <span class="label">当前播放：</span>
      <span class="text">{{ currentSegment.text }}</span>
    </div>

    <!-- 自动滚动开关 -->
    <div v-if="showText" class="auto-scroll-toggle">
      <label>
        <input type="checkbox" v-model="autoScrollEnabled">
        自动滚动
      </label>
    </div>

    <!-- 音频控制区 -->
    <div class="audio-player">
      <audio
        ref="audioPlayer"
        :src="audioUrl"
        @timeupdate="onTimeUpdate"
        @loadedmetadata="onLoadedMetadata"
        @play="onPlay"
        @pause="onPause"
        @ended="onEnded"
        controls
        controlsList="nodownload"
        oncontextmenu="return false"
      ></audio>

      <div class="player-controls">
        <button @click="togglePlayPause" class="control-btn">
          {{ isPlaying ? '⏸️' : '▶️' }}
        </button>
        <button @click="restartPlayback" class="control-btn">↺</button>
        <button @click="download" class="control-btn">⤓</button>

        <div class="volume-control">
          <span class="volume-icon">🔊</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            v-model="volume"
            @input="setVolume"
            class="volume-slider"
          />
        </div>

        <div class="speed-controls">
          <span class="speed-label">倍速：</span>
          <button
            v-for="speed in speeds"
            :key="speed"
            @click="setSpeed(speed)"
            :class="['speed-btn', { 'active': currentSpeed === speed }]"
          >
            {{ speed }}x
          </button>
        </div>


      </div>


    </div>
  </div>
</template>

<script>
export default {
  props: {
    voiceId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      audioUrl: null,
      blobUrl: null,
      audioElement: null,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      textSegments: [],
      currentSegmentIndex: -1,
      currentCharIndex: 0,
      autoScrollEnabled: true,
      speeds: [0.5, 0.75, 1.0, 1.25, 1.5],
      currentSpeed: 1.0,
      volume: 1.0,
      isDark: false,
      showText: false
    };
  },
  computed: {
    currentSegment() {
      return this.currentSegmentIndex >= 0 ? this.textSegments[this.currentSegmentIndex] : null;
    }
  },
  watch: {
    audioUrl(newUrl) {
      if (newUrl) {
        this.$nextTick(() => {
          this.initAudio();
        });
      }
    },
    voiceId(id) {
      this.loadAudio(id);
      this.getRecordInfo();
    }
  },
  mounted() {
    this.loadAudio(this.voiceId);
    this.getRecordInfo();
    if (this.$parent && this.$parent.isDark !== undefined) {
      this.isDark = this.$parent.isDark;
    }
  },
  methods: {
    loadAudio(id) {
      if (this.blobUrl) {
        window.URL.revokeObjectURL(this.blobUrl);
        this.blobUrl = null;
      }
      this.$downloadVoice({
        method: 'GET',
        url: `/contact/getIntellVoice/${id}`,
        responseType: 'blob'
      }).then((res) => {
        const blob = new Blob([res.data]);
        this.blobUrl = window.URL.createObjectURL(blob);
        this.audioUrl = this.blobUrl;
      });
    },
    initAudio() {
      this.audioElement = this.$refs.audioPlayer;
    },
    getRecordInfo() {
      this.$ajax({
        method: 'GET',
        url: '/caseDetail/getRecordInfo',
        params: { 'voiceId': this.voiceId }
      }).then((res) => {
        this.textSegments = res.data;
      });
    },
    // 录音下载
    download() {
      this.$downloadVoice({
        method: 'GET',
        url: `/contact/download/${this.voiceId}`,
        responseType: 'blob'
      }).then((res) => {
        console.warn(res);
        const blob = new Blob([res.data]);
        const fileName = `${Date.parse(new Date())}.wav`;
        if (window.navigator.msSaveOrOpenBlob) {
          navigator.msSaveBlob(blob, fileName);
        } else {
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
          // 释放内存
          window.URL.revokeObjectURL(link.href);
        }
      });
    },
    processTextSegments() {
      if (!this.textContent || (this.segments && this.segments.length)) return;

      const sentences = this.textContent.match(/[^。！？]*[。！？]/g) || [this.textContent];

      if (!this.duration) {
        return;
      }

      this.textSegments = this.estimateTimeSegments(sentences);
    },

    estimateTimeSegments(sentences) {
      // const totalChars = sentences.join('').length;
      const segments = [];
      let currentTime = 0;

      sentences.forEach((sentence) => {
        const charCount = sentence.length;
        const duration = charCount / 3.5;

        const chars = sentence.split('').map((char, index) => ({
          char,
          time: currentTime + ((index / charCount) * duration)
        }));

        segments.push({
          text: sentence,
          startTime: currentTime,
          endTime: currentTime + duration,
          chars
        });

        currentTime += duration;
      });

      return segments;
    },

    onTimeUpdate() {
      this.currentTime = this.audioElement.currentTime;
      this.updateHighlightPosition();

      // 自动滚动逻辑
      if (this.autoScrollEnabled && this.currentSegmentIndex !== -1) {
        this.scrollToCurrentSegment();
      }
    },

    onLoadedMetadata() {
      if (this.audioElement && !isNaN(this.audioElement.duration) && this.audioElement.duration > 0) {
        this.duration = this.audioElement.duration;
      } else {
        this.duration = 0;
      }
      if (this.textContent && (!this.segments || !this.segments.length)) {
        this.processTextSegments();
      }
    },

    updateHighlightPosition() {
      const time = this.currentTime;

      let newSegmentIndex = -1;
      for (let i = 0; i < this.textSegments.length; i++) {
        const segment = this.textSegments[i];
        if (time >= segment.startTime && time <= segment.endTime) {
          newSegmentIndex = i;
          break;
        }
      }

      if (newSegmentIndex !== -1) {
        this.currentSegmentIndex = newSegmentIndex;

        const segment = this.textSegments[newSegmentIndex];
        const segmentProgress = (time - segment.startTime) / (segment.endTime - segment.startTime);
        this.currentCharIndex = Math.floor(segmentProgress * segment.text.length);
      } else if (this.textSegments.length > 0 && time > this.textSegments[this.textSegments.length - 1].endTime) {
        this.currentSegmentIndex = -1;
        this.currentCharIndex = -1;
      }
    },

    isSegmentActive(segment) {
      return this.currentTime >= segment.startTime && this.currentTime <= segment.endTime;
    },

    isSegmentPassed(segment) {
      return this.currentTime > segment.endTime;
    },

    isCharHighlighted(segment, charIndex) {
      if (!this.isSegmentActive(segment)) return false;

      if (segment.chars && segment.chars.length) {
        return this.currentTime >= segment.chars[charIndex].time;
      }
      return charIndex <= this.currentCharIndex;
    },

    setVolume() {
      if (this.audioElement) {
        this.audioElement.volume = this.volume;
      }
    },

    seekToSegment(segment) {
      if (!this.audioElement || !segment) return;
      this.audioElement.currentTime = segment.startTime;
      const playAudio = () => {
        if (!this.isPlaying) {
          this.audioElement.play().catch((err) => {
            console.warn('播放失败：', err);
          });
        }
      };
      if (this.audioElement.readyState >= 2) {
        playAudio();
      } else {
        this.audioElement.addEventListener('canplay', playAudio, { once: true });
      }
    },

    togglePlayPause() {
      if (this.isPlaying) {
        this.audioElement.pause();
      } else {
        this.audioElement.play();
      }
    },

    restartPlayback() {
      this.audioElement.currentTime = 0;
      this.audioElement.play();
    },

    setSpeed(speed) {
      this.currentSpeed = speed;
      if (this.audioElement) {
        this.audioElement.playbackRate = speed;
      }
    },

    onPlay() {
      this.isPlaying = true;
      this.showText = true;
    },

    onPause() {
      this.isPlaying = false;
    },

    onEnded() {
      this.isPlaying = false;
      this.showText = false;
      this.currentSegmentIndex = -1;
      this.currentCharIndex = -1;
    },

    scrollToCurrentSegment() {
      this.$nextTick(() => {
        const container = this.$refs.textContainer;
        if (!container) return;

        const activeElement = container.querySelector('.text-segment.active');

        if (activeElement) {
          activeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      });
    },

    formatTime(seconds) {
      if (seconds === undefined || seconds === null || isNaN(seconds) || seconds < 0 || seconds === Infinity) return '00:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },

    pauseAudio() {
      if (this.audioElement) {
        this.audioElement.pause();
      }
    },

    toggleTheme() {
      this.isDark = !this.isDark;
    }
  },
  beforeDestroy() {
    this.pauseAudio();
    if (this.blobUrl) {
      window.URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  }
};
</script>

<style scoped>
/* 基础样式变量 */
:root {
  --primary-color: #667eea;
  --primary-light: #764ba2;
  --secondary-color: #f0f4ff;
  --text-color: #1e293b;
  --text-secondary: #64748b;
  --bg-color: #f8f9ff;
  --bg-secondary: #ffffff;
  --border-color: #e2e8f0;
  --shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

/* 深色模式变量 */
.dark-theme {
  --primary-color: #818cf8;
  --primary-light: #a78bfa;
  --secondary-color: #1e293b;
  --text-color: #f1f5f9;
  --text-secondary: #94a3b8;
  --bg-color: #0f172a;
  --bg-secondary: #1e293b;
  --border-color: #334155;
  --shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.audio-text-sync {
  max-width: 100%;
  margin: 0;
  padding: 8px;
  font-family: 'Microsoft YaHei', sans-serif;
  border-radius: 12px;
  background: var(--bg-color);
  box-shadow: var(--shadow);
  color: var(--text-color);
  transition: all 0.3s ease;
}

.audio-player {
  background: linear-gradient(135deg, #e0e7ff 0%, #dbeafe 100%);
  padding: 16px;
  border-radius: 12px;
  margin-top: 12px;
  color: var(--text-color);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.dark-theme .audio-player {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.audio-player::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  animation: shimmer 3s ease-in-out infinite;
}

@keyframes shimmer {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(10%, 10%); }
}

audio {
  width: 100%;
  margin-bottom: 16px;
  border-radius: 8px;
  height: 40px;
}

audio::-webkit-media-controls-download-button {
  display: none !important;
}

audio::-webkit-media-controls-volume-slider {
  display: none !important;
}

audio::-webkit-media-controls-mute-button {
  display: none !important;
}

audio::-webkit-media-controls-volume-control-container {
  display: none !important;
}

audio::-webkit-media-controls-playback-rate-button {
  display: none !important;
}

.player-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
}

.control-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(102, 126, 234, 0.1);
  color: var(--text-color);
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.dark-theme .control-btn {
  background: rgba(129, 140, 248, 0.2);
  color: var(--text-color);
}

.control-btn::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0);
  background: rgba(102, 126, 234, 0.3);
  border-radius: 50%;
  transition: transform 0.4s ease;
}

.control-btn:active::after {
  transform: translate(-50%, -50%) scale(2);
}

.control-btn:hover {
  background: rgba(102, 126, 234, 0.2);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.dark-theme .control-btn:hover {
  background: rgba(129, 140, 248, 0.3);
  box-shadow: 0 4px 12px rgba(129, 140, 248, 0.3);
}

.control-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.15);
}

.volume-control {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 8px;
}

.volume-icon {
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: var(--primary-color);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.volume-icon:hover {
  transform: scale(1.2);
  color: var(--primary-light);
  text-shadow: 0 2px 4px rgba(102, 126, 234, 0.4);
}

.volume-slider {
  width: 90px;
  height: 6px;
  -webkit-appearance: none;
  appearance: none;
  background: linear-gradient(to right, var(--primary-color), rgba(102, 126, 234, 0.2));
  border-radius: 3px;
  outline: none;
  cursor: pointer;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.dark-theme .volume-slider {
  background: linear-gradient(to right, var(--primary-color), rgba(129, 140, 248, 0.3));
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.3);
}

.volume-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 3px 8px rgba(102, 126, 234, 0.4);
  border: 2px solid white;
  transition: all 0.3s ease;
}

.volume-slider::-webkit-slider-thumb:hover {
  transform: scale(1.3);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.6);
}

.volume-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 3px 8px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
}

.volume-slider::-moz-range-thumb:hover {
  transform: scale(1.3);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.6);
}

.speed-controls {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}

.speed-label {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
}

.speed-btn {
  padding: 6px 12px;
  border: 2px solid rgba(102, 126, 234, 0.2);
  border-radius: 6px;
  background: rgba(102, 126, 234, 0.05);
  color: var(--text-color);
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  font-weight: 500;
  position: relative;
  overflow: hidden;
}

.dark-theme .speed-btn {
  border-color: rgba(129, 140, 248, 0.3);
  background: rgba(129, 140, 248, 0.1);
}

.speed-btn::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0);
  background: rgba(102, 126, 234, 0.3);
  border-radius: 4px;
  transition: transform 0.4s ease;
}

.speed-btn:active::after {
  transform: translate(-50%, -50%) scale(2);
}

.speed-btn:hover {
  background: rgba(102, 126, 234, 0.15);
  border-color: rgba(102, 126, 234, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.dark-theme .speed-btn:hover {
  background: rgba(129, 140, 248, 0.2);
  border-color: rgba(129, 140, 248, 0.5);
  box-shadow: 0 4px 12px rgba(129, 140, 248, 0.3);
}

.speed-btn.active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: white;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transform: scale(1.05);
}

.text-sync-container {
  height: 320px;
  overflow-y: auto;
  padding: 8px;
  background: var(--bg-secondary);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  margin-bottom: 8px;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.text-sync-container::-webkit-scrollbar {
  width: 6px;
}

.text-sync-container::-webkit-scrollbar-track {
  background: rgba(102, 126, 234, 0.05);
  border-radius: 3px;
}

.text-sync-container::-webkit-scrollbar-thumb {
  background: var(--primary-color);
  border-radius: 3px;
  transition: all 0.3s ease;
}

.text-sync-container::-webkit-scrollbar-thumb:hover {
  background: var(--primary-light);
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
}

.text-segment {
  padding: 8px 12px;
  margin: 6px 0;
  background: var(--bg-secondary);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 80%;
  width: fit-content;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.text-segment::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.text-segment:hover::before {
  opacity: 1;
}

.text-segment.robot {
  align-items: flex-start;
  background: linear-gradient(135deg, #e3f2fd 0%, #dbeafe 100%);
  border-left: 4px solid #2563eb;
  border-radius: 12px 12px 12px 4px;
}

.dark-theme .text-segment.robot {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-left-color: #4f46e5;
}

.text-segment.customer {
  align-items: flex-end;
  align-self: flex-end;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-right: 4px solid #f59e0b;
  border-left: none;
  border-radius: 12px 12px 4px 12px;
  margin-left: auto;
}

.dark-theme .text-segment.customer {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-right-color: #d97706;
}

.text-segment:hover {
  transform: translateX(4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.text-segment.active {
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.2);
  transform: scale(1.02);
  z-index: 1;
}

.dark-theme .text-segment.active {
  box-shadow: 0 6px 20px rgba(129, 140, 248, 0.3);
}

.text-segment.robot.active {
  box-shadow: 0 6px 20px rgba(37, 99, 235, 0.3);
  border-left-width: 6px;
}

.dark-theme .text-segment.robot.active {
  box-shadow: 0 6px 20px rgba(79, 70, 229, 0.4);
}

.text-segment.customer.active {
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.3);
  border-right-width: 6px;
}

.dark-theme .text-segment.customer.active {
  box-shadow: 0 6px 20px rgba(217, 119, 6, 0.4);
}

.text-segment.passed {
  opacity: 0.5;
  transform: scale(0.98);
}

.segment-header {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  position: relative;
  z-index: 1;
}

.segment-role {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-color);
  padding: 3px 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.7);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.dark-theme .segment-role {
  background: rgba(129, 140, 248, 0.2);
  color: var(--text-color);
}

.segment-time {
  font-size: 10px;
  color: var(--text-secondary);
  font-family: 'Courier New', monospace;
  font-weight: 500;
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 4px;
}

.dark-theme .segment-time {
  background: rgba(129, 140, 248, 0.15);
}

.segment-content {
  font-size: 14px;
  line-height: 1.7;
  color: var(--text-color);
  width: 100%;
  position: relative;
  z-index: 1;
}

.char {
  display: inline-block;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0 1px;
  border-radius: 2px;
}

.char-highlight {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  color: white;
  border-radius: 6px;
  transform: scale(1.02);
  font-weight: 700;
  padding: 3px 6px;
  margin: 0 1px;
  animation: charPop 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 3px 12px rgba(102, 126, 234, 0.5);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

@keyframes charPop {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 rgba(102, 126, 234, 0);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.7);
    opacity: 1;
  }
  100% {
    transform: scale(1.02);
    box-shadow: 0 3px 12px rgba(102, 126, 234, 0.5);
    opacity: 1;
  }
}

.current-position {
  padding: 12px 16px;
  background: linear-gradient(135deg, #e8f4fd 0%, #d1e9fc 100%);
  border-radius: 8px;
  border-left: 4px solid #2196F3;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.15);
  transition: all 0.3s ease;
}

.dark-theme .current-position {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-left-color: #4f46e5;
  box-shadow: 0 2px 8px rgba(79, 70, 229, 0.2);
}

.current-position:hover {
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.25);
  transform: translateY(-1px);
}

.dark-theme .current-position:hover {
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
}

.current-position .label {
  font-weight: 600;
  color: #1976D2;
  margin-right: 8px;
  font-size: 13px;
}

.dark-theme .current-position .label {
  color: #818cf8;
}

.current-position .text {
  color: var(--text-color);
  font-size: 13px;
}

.auto-scroll-toggle {
  margin-top: 10px;
  padding: 10px 14px;
  background: var(--bg-secondary);
  border-radius: 8px;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  border: 1px solid var(--border-color);
}

.auto-scroll-toggle:hover {
  background: var(--bg-secondary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.auto-scroll-toggle label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 500;
  color: var(--text-color);
}

.auto-scroll-toggle input {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: var(--primary-color);
}

@media (max-width: 768px) {
  .audio-text-sync {
    padding: 6px;
  }

  .audio-player {
    padding: 12px;
  }

  .player-controls {
    gap: 6px;
  }

  .control-btn {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }

  .volume-slider {
    width: 60px;
  }

  .speed-controls {
    gap: 4px;
  }

  .speed-btn {
    padding: 5px 10px;
    font-size: 11px;
  }

  .text-sync-container {
    height: 280px;
    padding: 6px;
  }

  .text-segment {
    padding: 6px 10px;
    max-width: 90%;
  }

  .segment-content {
    font-size: 13px;
    line-height: 1.6;
  }
}
</style>



// WEBPACK FOOTER //
// src/page/main/case/fileText.vue